/**
 * Transient (optimistic) cart lines — pure state math, no React.
 *
 * The cart drawer renders SERVER props (`CartDrawerWrapper` → `lines`), so between
 * the click and the end of `router.refresh()` the authoritative list is stale: the
 * first add showed "your bag is empty" while the line was already being created.
 * A transient line bridges exactly that window.
 *
 * The transient line is NEVER removed on a timer. Each entry carries the
 * `expectedQuantity` — the authoritative quantity of its variant that proves the add
 * landed — and is dropped only once the server props reach it (`reconcilePendingEntries`).
 * That keeps the handover honest for slow mutations AND for several quick adds of the
 * same variant, because every attempt gets its own expectation.
 */

import type {
	CartAddSurface,
	CartExperimentVariant,
	CartInternalSource,
} from "@/lib/analytics/add-to-cart-events";
import { getPurchasableVariantDetails } from "@/lib/products/purchasable-variant";

/** Local vocabulary: consumers read `entry.status`, they never name the union. */
type PendingCartLineStatus = "pending" | "confirmed" | "failed";

/**
 * Everything the shared runner needs to perform ONE add — stored verbatim on the
 * entry so a retry (from the drawer or the toast) can replay it without the retrying
 * component knowing anything about the surface it came from.
 */
export interface AddToCartRequest {
	channel: string;
	/** Raw Saleor global id. */
	variantId: string;
	quantity: number;
	productSlug?: string;
	productName: string;
	variantName?: string;
	internalSource?: CartInternalSource;
	internalSourceDetail?: string;
	addSurface?: CartAddSurface;
	experimentVariant?: CartExperimentVariant;
	/** Next-intl locale; falls back to the pathname locale when omitted. */
	locale?: string;
}

/**
 * Shape of a transient row (the task's `PendingCartLine` contract). Consumers work
 * with `PendingCartEntry`, which adds the bookkeeping the drawer needs.
 */
interface PendingCartLine {
	/**
	 * Identity of the transient row. For single adds this IS the `cart_add_attempt_id`
	 * of the attempt; batch adds (stack builder) share one analytics id across lines,
	 * so each line gets its own row id there.
	 */
	attemptId: string;
	variantId: string;
	productSlug: string;
	productName: string;
	variantName: string;
	dosageLabel?: string;
	packSize: number;
	quantity: number;
	status: PendingCartLineStatus;
	errorCode?: string;
}

export interface PendingCartEntry extends PendingCartLine {
	/**
	 * Stable identity of the ROW across its whole retry chain (the first attempt's id).
	 * `attemptId` changes with every retry; keying the drawer row on that remounts the
	 * DOM node, which resets the browser's own multi-click counter — the second click of
	 * a double-click then arrives as a fresh `detail: 1` click on a fresh button and is
	 * indistinguishable from a deliberate retry. Keying on the chain keeps the node.
	 */
	chainId: string;
	/**
	 * True once any attempt of this chain failed. Keeps the retry button MOUNTED (just
	 * disabled) while the next attempt runs, so the node — and with it the multi-click
	 * counter — survives a fail → retry → fail cycle.
	 */
	hasFailed: boolean;
	/**
	 * Authoritative quantity of `variantId` that ends this row's transient life.
	 * Optimistic on creation (quantity before the attempt + requested), replaced by
	 * the server-confirmed total once the add succeeded.
	 */
	expectedQuantity: number;
	request: AddToCartRequest;
}

/** Minimal projection of the authoritative Saleor lines the drawer renders. */
export interface AuthoritativeCartLine {
	variantId: string;
	quantity: number;
}

/** Saleor merges same-variant lines, but summing stays correct if it ever does not. */
export function authoritativeQuantity(lines: readonly AuthoritativeCartLine[], variantId: string): number {
	return lines.reduce((total, line) => (line.variantId === variantId ? total + line.quantity : total), 0);
}

/** Quantity of a variant already claimed by live (non-failed) transient rows. */
function claimedQuantity(entries: readonly PendingCartEntry[], variantId: string): number {
	return entries.reduce(
		(total, entry) =>
			entry.variantId === variantId && entry.status !== "failed" ? total + entry.quantity : total,
		0,
	);
}

/**
 * Optimistic expectation for a NEW attempt: what the cart holds today plus what
 * earlier in-flight attempts will add plus this attempt's own quantity. Two quick
 * adds of the same variant therefore expect 1 and 2 — the first authoritative
 * update retires only the first row.
 */
export function nextExpectedQuantity(
	entries: readonly PendingCartEntry[],
	lines: readonly AuthoritativeCartLine[],
	request: AddToCartRequest,
): number {
	return (
		authoritativeQuantity(lines, request.variantId) +
		claimedQuantity(entries, request.variantId) +
		request.quantity
	);
}

export function createPendingEntry(
	request: AddToCartRequest,
	attemptId: string,
	entries: readonly PendingCartEntry[],
	lines: readonly AuthoritativeCartLine[],
	/** Retry: keeps the row (and its DOM node) identical across the chain. */
	chain?: { chainId: string; hasFailed: boolean },
): PendingCartEntry {
	const variantName = request.variantName?.trim() ?? "";
	// Same parsers as PDP/pricing/analytics, so the transient row reads like the real one.
	const details = getPurchasableVariantDetails({ id: request.variantId, name: variantName });

	return {
		attemptId,
		chainId: chain?.chainId ?? attemptId,
		hasFailed: chain?.hasFailed ?? false,
		variantId: request.variantId,
		productSlug: request.productSlug ?? "",
		productName: request.productName,
		variantName,
		...(details.doseLabel ? { dosageLabel: details.doseLabel } : {}),
		packSize: details.packCount,
		quantity: request.quantity,
		status: "pending",
		expectedQuantity: nextExpectedQuantity(entries, lines, request),
		request,
	};
}

export function appendPendingEntry(
	entries: readonly PendingCartEntry[],
	entry: PendingCartEntry,
): PendingCartEntry[] {
	return [...entries, entry];
}

/**
 * Retry: swap the failed row for the new attempt IN PLACE so the row does not jump
 * to the bottom of the drawer while the visitor is looking at it.
 *
 * A stale id is a NO-OP (same array back), never an append: the second click of a
 * double-click no longer finds the row the first click already replaced, and
 * appending there would post the line twice.
 */
export function replacePendingEntry(
	entries: readonly PendingCartEntry[],
	previousAttemptId: string,
	entry: PendingCartEntry,
): PendingCartEntry[] {
	const index = entries.findIndex((candidate) => candidate.attemptId === previousAttemptId);
	if (index < 0) return entries as PendingCartEntry[];
	const next = [...entries];
	next[index] = entry;
	return next;
}

export type PendingCartLineSettlement =
	| {
			status: "confirmed";
			/**
			 * Authoritative total quantity of the variant AFTER the add, as reported by
			 * the API. Beats the optimistic expectation whenever it is available.
			 */
			confirmedQuantity?: number;
	  }
	| { status: "failed"; errorCode: string };

export function settlePendingEntry(
	entries: readonly PendingCartEntry[],
	attemptId: string,
	settlement: PendingCartLineSettlement,
): PendingCartEntry[] {
	let changed = false;
	const next = entries.map((entry) => {
		if (entry.attemptId !== attemptId) return entry;
		changed = true;
		if (settlement.status === "failed") {
			return { ...entry, status: "failed" as const, hasFailed: true, errorCode: settlement.errorCode };
		}
		const confirmed = settlement.confirmedQuantity;
		const expectedQuantity =
			typeof confirmed === "number" && Number.isFinite(confirmed) && confirmed > 0
				? confirmed
				: entry.expectedQuantity;
		const { errorCode: _dropped, ...rest } = entry;
		return { ...rest, status: "confirmed" as const, expectedQuantity };
	});
	return changed ? next : (entries as PendingCartEntry[]);
}

/** A confirmed row is done as soon as the server props carry its expected quantity. */
export function isPendingEntrySatisfied(
	entry: PendingCartEntry,
	lines: readonly AuthoritativeCartLine[],
): boolean {
	return (
		entry.status === "confirmed" && authoritativeQuantity(lines, entry.variantId) >= entry.expectedQuantity
	);
}

/**
 * Drop the rows the authoritative props have caught up with. Returns the SAME array
 * instance when nothing changed so the provider's `setState` bails out instead of
 * re-rendering the drawer on every server refresh.
 */
export function reconcilePendingEntries(
	entries: readonly PendingCartEntry[],
	lines: readonly AuthoritativeCartLine[],
): PendingCartEntry[] {
	const next = entries.filter((entry) => !isPendingEntrySatisfied(entry, lines));
	return next.length === entries.length ? (entries as PendingCartEntry[]) : next;
}

/** Failed rows are the visitor's business only while the drawer is open. */
export function dropFailedPendingEntries(entries: readonly PendingCartEntry[]): PendingCartEntry[] {
	const next = entries.filter((entry) => entry.status !== "failed");
	return next.length === entries.length ? (entries as PendingCartEntry[]) : next;
}
